
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FoodItem, MealSlot } from '@/types/meal-plan';
import { ClientIntakeData } from '@/components/meal-planning/ClientIntakeForm';
import { suggestMealSwap } from '@/utils/meal-suggestions';
import { Loader2, Star, CheckCircle } from 'lucide-react';

interface MealSwapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mealSlot: MealSlot;
  originalFood: FoodItem;
  onSwapMeal: (newFood: FoodItem) => void;
  clientPreferences?: Partial<ClientIntakeData>;
}

interface EnhancedFoodItem extends FoodItem {
  reasoning?: string;
}

const MealSwapModal: React.FC<MealSwapModalProps> = ({
  open,
  onOpenChange,
  mealSlot,
  originalFood,
  onSwapMeal,
  clientPreferences
}) => {
  const [alternatives, setAlternatives] = useState<EnhancedFoodItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadAlternatives();
    }
  }, [open, mealSlot]);

  const loadAlternatives = async () => {
    setLoading(true);
    try {
      const suggestions = await suggestMealSwap(mealSlot, clientPreferences) as EnhancedFoodItem[];
      setAlternatives(suggestions);
    } catch (error) {
      console.error('Error loading meal alternatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = (newFood: FoodItem) => {
    onSwapMeal(newFood);
    onOpenChange(false);
  };

  const getCalorieDifference = (newFood: FoodItem) => {
    const diff = newFood.calories - originalFood.calories;
    if (Math.abs(diff) < 10) return null;
    return diff > 0 ? `+${diff} kcal` : `${diff} kcal`;
  };

  const getProteinDifference = (newFood: FoodItem) => {
    const diff = newFood.protein - originalFood.protein;
    if (Math.abs(diff) < 2) return null;
    return diff > 0 ? `+${diff}g protein` : `${diff}g protein`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Swap "{originalFood.name}"</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-muted/30 p-3 rounded-lg">
            <div className="text-sm text-gray-600 mb-2">Current meal:</div>
            <div className="font-medium">{originalFood.name}</div>
            <div className="text-sm text-gray-500">
              {originalFood.calories} kcal | {originalFood.protein}g protein | {originalFood.serving}
            </div>
          </div>
          
          <div className="text-sm text-gray-600">
            Choose a similar meal based on your preferences and nutritional needs:
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Finding personalized alternatives...</span>
            </div>
          ) : (
            <div className="grid gap-4">
              {alternatives.map((food, index) => (
                <Card key={food.id} className="cursor-pointer hover:bg-muted/30 transition-colors border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 space-y-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{food.name}</h4>
                            {index === 0 && (
                              <Star className="h-4 w-4 text-yellow-500 fill-current" aria-label="Best match" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{food.serving}</p>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{food.calories} kcal</span>
                            {getCalorieDifference(food) && (
                              <span className={`text-xs px-1 rounded ${
                                food.calories > originalFood.calories ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {getCalorieDifference(food)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span>{food.protein}g protein</span>
                            {getProteinDifference(food) && (
                              <span className={`text-xs px-1 rounded ${
                                food.protein > originalFood.protein ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {getProteinDifference(food)}
                              </span>
                            )}
                          </div>
                          <span className="text-gray-600">{food.carbs}g carbs</span>
                          <span className="text-gray-600">{food.fat}g fat</span>
                        </div>
                        
                        {food.reasoning && (
                          <div className="bg-blue-50 p-2 rounded text-xs text-blue-800 flex items-start gap-1">
                            <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            <span>{food.reasoning}</span>
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        onClick={() => handleSwap(food)}
                        size="sm"
                        className="flex-shrink-0"
                      >
                        Swap
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {alternatives.length === 0 && !loading && (
                <div className="text-center py-6 text-gray-500">
                  <div className="text-sm">No suitable alternatives found for this meal.</div>
                  <div className="text-xs mt-1">Try adjusting your dietary preferences or meal timing.</div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MealSwapModal;
